import type { KeycloakTokenParsed } from 'keycloak-js'

import { AuthUser, IdPClaims } from '../../machine/types'

export interface ClaimMapperOptions {
    /**
     * Keycloak resource (client) whose role list represents the user's
     * roles in our app. Default: `aos-oidc`.
     */
    rolesResource?: string
    /**
     * Map a Keycloak parsed token to {@link AuthUser}. Override for
     * apps with non-standard claim shapes. The default reads `aosId`,
     * `name`, `displayName`, `firebaseId`, `email`.
     */
    mapUser?: (parsed: KeycloakTokenParsed) => AuthUser
}

const DEFAULT_RESOURCE = 'aos-oidc'

/**
 * Sentinel `id` for tokens issued before the backend has provisioned the
 * user record. A first-time login round-trips Keycloak → backend
 * `initUser` → forced refresh, and the *first* token in that chain has no
 * `aosId` claim. Apps detect this sentinel and re-run the provisioning
 * dance; throwing here would land the FSM in `unauthenticated` and
 * silently strand the user on a blank screen.
 *
 * Exported via the package barrel so apps can match against it without
 * hard-coding the magic number.
 */
export const UNPROVISIONED_AOS_ID = 0

/**
 * Keycloak emits the `aosId` claim as a **string** (the legacy
 * `useAuthInfo` did `parseInt(parsed.aosId, 10)`). Accept both number and
 * numeric string; anything else (including non-numeric strings) collapses
 * to the unprovisioned sentinel so the host can run the first-login
 * dance.
 */
const coerceAosId = (raw: unknown): number => {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return raw
    }
    if (typeof raw === 'string' && raw.length > 0) {
        const parsed = Number(raw)
        if (Number.isFinite(parsed)) {
            return parsed
        }
    }
    return UNPROVISIONED_AOS_ID
}

const defaultMapUser = (parsed: KeycloakTokenParsed): AuthUser => ({
    id: coerceAosId((parsed as { aosId?: unknown }).aosId),
    name: (parsed as { name?: string }).name ?? '',
    displayName:
        (parsed as { displayName?: string }).displayName ?? (parsed as { name?: string }).name,
    firebaseId: (parsed as { firebaseId?: string }).firebaseId,
    email: (parsed as { email?: string }).email,
})

/**
 * Airport membership from the `groups` claim. Keycloak's group-membership
 * mapper emits full paths (`/airports/TLL`, `/airports/TLL/subgroup`);
 * the airport is the path segment right after `airports`. Non-airport
 * groups (`/AOS testers`, custom groups) are ignored.
 */
const airportsFromGroups = (groups: readonly string[]): string[] => {
    const airports = new Set<string>()
    for (const group of groups) {
        const segments = group.split('/').filter(s => s.length > 0)
        if (segments[0] === 'airports' && segments[1]) {
            airports.add(segments[1])
        }
    }
    return [...airports]
}

/** Build an {@link IdPClaims} object from a parsed Keycloak token. */
export const claimsFromKeycloakToken = (
    parsed: KeycloakTokenParsed,
    options: ClaimMapperOptions = {},
): IdPClaims => {
    const resource = options.rolesResource ?? DEFAULT_RESOURCE
    const mapUser = options.mapUser ?? defaultMapUser
    const access = (parsed as { resource_access?: Record<string, { roles?: string[] }> })
        .resource_access
    const roles = access?.[resource]?.roles ?? []
    const groups = (parsed as { groups?: string[] }).groups ?? []
    const exp = (parsed as { exp?: number }).exp ?? 0
    return {
        user: mapUser(parsed),
        roles,
        airports: airportsFromGroups(groups),
        expiresAt: exp * 1000,
    }
}
